
/**
 * Valuation Service
 * Updated: 2025-05-11 - Improved CORS handling and error recovery
 * Updated: 2025-11-08 - Added Turnstile token parameter for bot protection
 */
import { supabase } from "@/integrations/supabase/client";
import { getDirectValuation } from "@/services/valuation/directValuationService";

export async function getValuation(
  vin: string,
  mileage: number | string,
  gearbox: string = "manual",
  turnstileToken: string | null = null,
  options: { requestId?: string } = {}
) {
  console.log('[Valuation][' + (options.requestId || 'N/A') + '] Requesting valuation for:', {
    vin,
    mileage,
    gearbox,
    hasTurnstileToken: !!turnstileToken,
    debug: options.requestId,
    timestamp: new Date().toISOString()
  });

  // Normalize mileage to number
  const normalizedMileage = typeof mileage === 'string' ? parseInt(mileage, 10) : mileage;

  // First try the direct API approach which uses our proxy to avoid CORS issues
  try {
    console.log('[Valuation] Attempting direct API call');
    const directResult = await getDirectValuation(vin, normalizedMileage, gearbox, turnstileToken);
    
    if (directResult.success) {
      console.log('[Valuation] Direct API call successful');
      return directResult;
    }
    
    // If manual valuation is required, return early - DO NOT fallback
    // The Turnstile token is already consumed, fallback would fail with 403
    if (directResult.requiresManualValuation) {
      console.log('[Valuation] Manual valuation required - returning result without fallback');
      return {
        success: false,
        requiresManualValuation: true,
        code: directResult.code || 'NO_DATA_AVAILABLE',
        error: directResult.error || 'No valuation data available',
        method: directResult.method
      };
    }
    
    console.log('[Valuation] Direct API call failed, trying edge function');
  } catch (error) {
    console.error('[Valuation] Direct API call error:', error);
    console.log('[Valuation] Direct API call failed, trying edge function');
  }

  // Fallback to edge function if direct API call fails
  try {
    const { data, error } = await supabase.functions.invoke('get-vehicle-valuation', {
      body: { vin, mileage: normalizedMileage, gearbox, turnstileToken }
    });

    if (error) {
      console.error('[Valuation] Edge function error:', error);
      console.error('[Valuation] Error message:', error.message);
      console.error('[Valuation] Error context:', error.context);
      throw error;
    }

    // Check if manual valuation is required
    if (data.requiresManualValuation) {
      console.log('[Valuation] Manual valuation required, returning complete response');
      return {
        success: false,
        requiresManualValuation: true,
        code: data.code,
        error: data.error,
        vin: data.vin,
        mileage: data.mileage,
        gearbox: gearbox,
        method: 'edge-function'
      };
    }

    // Normal successful valuation
    return {
      success: true,
      data: data.data,
      method: 'edge-function'
    };
  } catch (error) {
    console.error('[Valuation] Edge function error:', error);
    console.error('[Valuation] Error message:', error.message);
    console.error('[Valuation] Error context:', error?.context || {});
    console.error('[Valuation] All valuation methods failed');
    console.error('[Valuation] Service error:', error);
    
    return {
      success: false,
      error: 'Nie udało się uzyskać wyceny pojazdu'
    };
  }
}

/**
 * Clean up any saved valuation data
 */
export function cleanupValuationData() {
  try {
    localStorage.removeItem('valuationData');
    localStorage.removeItem('valuationTimestamp');
  } catch (e) {
    console.error('Error clearing valuation data:', e);
  }
}
